import { useState } from "react";
import {
  Box,
  Button,
  ClickAwayListener,
  ListItemIcon,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import type { BoxProps } from "@mui/material";
import DownIcon from "@mui/icons-material/ArrowDropDown";

type SelectButtonProps<T extends React.Key> = {
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: React.ReactNode; icon: React.ReactNode }[];
} & Omit<BoxProps, "value" | "onChange">;

const SelectButton = <T extends React.Key>(props: SelectButtonProps<T>) => {
  const { value, onChange, options, ...boxProps } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleSelect = (value: T) => {
    onChange(value);
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <Box component="span" {...boxProps}>
        <Button
          variant="outlined"
          startIcon={options.find((option) => option.value === value)?.icon}
          endIcon={<DownIcon />}
          onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
          sx={{ "& .MuiButton-endIcon": { ml: 1.5, mr: -1 } }}
        >
          {options.find((option) => option.value === value)?.label}
        </Button>

        <Popper open={Boolean(anchorEl)} anchorEl={anchorEl}>
          <Paper>
            <MenuList>
              {options.map((option) => (
                <MenuItem key={option.value} onClick={() => handleSelect(option.value)}>
                  <ListItemIcon>{option.icon}</ListItemIcon>
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default SelectButton;
